import React, { useState, useEffect, useCallback } from "react";
import {
  Eye,
  Pencil,
  Trash2,
  Plus,
  X,
  Quote,
  Image as ImageIcon,
  Video,
  User,
  AlertTriangle,
  Loader2,
  Search,
  Hash,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Apirequests } from "../../utils/ApiRequests";
import { ErrorTost, SuccessTost } from "../../utils/ToastHelper";

/* ------------------------------------------------------------------ *
 *  CONFIG
 *  Endpoints (from your controller):
 *    GET    /testimonials/admin      -> getAllTestimonials (admin)
 *    POST   /testimonials/create     -> createTestimonial
 *    PUT    /testimonials/edit/:id   -> updateTestimonial
 *    DELETE /delete/:id              -> deleteTestimonial
 *  Falls back to in-memory demo data if the API can't be reached.
 * ------------------------------------------------------------------ */
const PAGE_SIZE = 10;

const TYPE_META = {
  text: { label: "Text", icon: Quote, badge: "bg-violet-50 text-violet-700 ring-violet-100" },
  image: { label: "Image", icon: ImageIcon, badge: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  video: { label: "Video", icon: Video, badge: "bg-rose-50 text-rose-600 ring-rose-100" },
};

const DEMO_DATA = [
  {
    _id: "demo1",
    type: "text",
    serialNo: 1,
    name: "Ananya Rao",
    designation: "Yoga teacher, Bengaluru",
    avatar: "",
    text:
      "The Silva Method completely changed how I handle stress before a class. I walk in calm, centred, and fully present. Six months in and I'm sleeping better than I have in years.",
    imgUrl: "",
    video: "",
    status: "active",
  },
  {
    _id: "demo2",
    type: "image",
    serialNo: 2,
    name: "Marco Bianchi",
    designation: "Verified graduate",
    avatar: "",
    text: "",
    imgUrl: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=900",
    video: "",
    status: "inactive",
  },
  {
    _id: "demo3",
    type: "video",
    serialNo: 3,
    name: "Priya Menon",
    designation: "BLS Graduate 2025",
    avatar: "",
    text: "",
    imgUrl: "",
    video: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    status: "active",
  },
];

const emptyForm = () => ({
  type: "text",
  serialNo: "",
  name: "",
  designation: "",
  avatar: "",
  text: "",
  imgUrl: "",
  video: "",
  status: "active",
});

/* ---------- small presentational helpers ---------- */

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.text;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${meta.badge}`}
    >
      <Icon size={13} />
      {meta.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const active = status !== "inactive"; // default to active
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
          : "bg-slate-100 text-slate-500 ring-slate-200"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Avatar({ name, src, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setFailed(true)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-700"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials || <User size={size * 0.5} />}
    </div>
  );
}

function getVideoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  return { kind: "video", src: url };
}

/* ---------- the view-panel content ---------- */

function TestimonialView({ item }) {
  if (!item) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <TypeBadge type={item.type} />
        <StatusBadge status={item.status} />
        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
          <Hash size={12} /> serial {item.serialNo}
        </span>
      </div>

      {item.type === "text" && (
        <figure className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <Quote className="mb-3 text-violet-400" size={22} />
          <blockquote className="text-[15px] leading-relaxed text-slate-700">
            {item.text || <span className="italic text-slate-400">No text provided.</span>}
          </blockquote>
        </figure>
      )}

      {item.type === "image" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
          {item.imgUrl ? (
            <img src={item.imgUrl} alt={item.name || "testimonial"} className="w-full object-cover" />
          ) : (
            <div className="flex h-48 items-center justify-center text-slate-400">
              <ImageIcon size={32} />
            </div>
          )}
        </div>
      )}

      {item.type === "video" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-black">
          {(() => {
            const v = getVideoEmbed(item.video);
            if (!v)
              return (
                <div className="flex h-48 items-center justify-center text-slate-400">
                  <Video size={32} />
                </div>
              );
            if (v.kind === "iframe")
              return (
                <iframe
                  src={v.src}
                  title="testimonial video"
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              );
            return <video src={v.src} controls className="aspect-video w-full" />;
          })()}
        </div>
      )}

      {/* person details */}
      <div className="flex items-center gap-3 border-t border-slate-100 pt-5">
        <Avatar name={item.name} src={item.avatar} size={48} />
        <div>
          <p className="font-medium text-slate-900">{item.name || "—"}</p>
          {item.designation && <p className="text-sm text-slate-500">{item.designation}</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------- the add / edit form ---------- */

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 placeholder:text-slate-400";

function StatusToggle({ value, onChange }) {
  const opts = [
    { v: "active", label: "Active", on: "bg-emerald-600 text-white" },
    { v: "inactive", label: "Inactive", on: "bg-slate-600 text-white" },
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
            value === o.v ? o.on : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TestimonialForm({ form, setForm, errors, onSubmit, saving }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Type" required>
          <select value={form.type} onChange={set("type")} className={inputCls}>
            <option value="text">Text</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </Field>
        <Field label="Serial no." required hint="Controls display order">
          <input
            type="number"
            value={form.serialNo}
            onChange={set("serialNo")}
            className={inputCls}
            placeholder="1"
          />
          {errors.serialNo && <span className="mt-1 block text-xs text-rose-500">{errors.serialNo}</span>}
        </Field>
      </div>

      <Field label="Status" hint="Inactive testimonials stay saved but are hidden on the site">
        <StatusToggle value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" required={form.type === "text"}>
          <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Full name" />
          {errors.name && <span className="mt-1 block text-xs text-rose-500">{errors.name}</span>}
        </Field>
        <Field label="Designation">
          <input
            value={form.designation}
            onChange={set("designation")}
            className={inputCls}
            placeholder="e.g. BLS Graduate"
          />
        </Field>
      </div>

      {form.type === "text" && (
        <>
          <Field label="Avatar URL" hint="Optional — initials shown if blank">
            <input value={form.avatar} onChange={set("avatar")} className={inputCls} placeholder="https://…" />
          </Field>
          <Field label="Review text" required>
            <textarea
              value={form.text}
              onChange={set("text")}
              rows={5}
              className={`${inputCls} resize-none`}
              placeholder="What did they say?"
            />
            {errors.text && <span className="mt-1 block text-xs text-rose-500">{errors.text}</span>}
          </Field>
        </>
      )}

      {form.type === "image" && (
        <Field label="Image URL" required hint="A screenshot or photo testimonial">
          <input value={form.imgUrl} onChange={set("imgUrl")} className={inputCls} placeholder="https://…" />
          {errors.imgUrl && <span className="mt-1 block text-xs text-rose-500">{errors.imgUrl}</span>}
          {form.imgUrl && (
            <img
              src={form.imgUrl}
              alt="preview"
              className="mt-3 max-h-40 rounded-lg border border-slate-200 object-cover"
            />
          )}
        </Field>
      )}

      {form.type === "video" && (
        <Field label="Video URL" required hint="YouTube, Vimeo, or a direct .mp4 link">
          <input value={form.video} onChange={set("video")} className={inputCls} placeholder="https://…" />
          {errors.video && <span className="mt-1 block text-xs text-rose-500">{errors.video}</span>}
        </Field>
      )}

      <button
        onClick={onSubmit}
        disabled={saving}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
      >
        {saving && <Loader2 size={16} className="animate-spin" />}
        {saving ? "Saving…" : "Save testimonial"}
      </button>
    </div>
  );
}

/* ---------- the sliding offcanvas shell ---------- */

function SidePanel({ open, title, subtitle, onClose, children }) {
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      {/* panel */}
      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="font-serif text-xl text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={20} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </aside>
    </>
  );
}

/* ---------- delete confirmation ---------- */

function ConfirmDelete({ item, onCancel, onConfirm, busy }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onCancel} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <AlertTriangle size={20} />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Delete this testimonial?</h3>
        <p className="mt-1.5 text-sm text-slate-500">
          {item.name ? `“${item.name}”` : `The ${item.type} testimonial`} will be removed permanently. This
          can't be undone.
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- pagination ---------- */

function Pagination({ page, totalPages, total, onPage }) {
  if (total === 0) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  // windowed page numbers with ellipsis
  const nums = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
      nums.push(p);
    } else if (nums[nums.length - 1] !== "…") {
      nums.push("…");
    }
  }

  const btn =
    "flex h-8 min-w-8 items-center justify-center rounded-lg border border-slate-200 px-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="flex flex-col items-center gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:justify-between">
      <p className="text-xs text-slate-500">
        Showing {start}–{end} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page === 1} className={btn} aria-label="Previous">
          <ChevronLeft size={16} />
        </button>

        {/* numbered pages on larger screens */}
        <div className="hidden items-center gap-1 sm:flex">
          {nums.map((n, i) =>
            n === "…" ? (
              <span key={`e${i}`} className="px-1 text-slate-400">
                …
              </span>
            ) : (
              <button
                key={n}
                onClick={() => onPage(n)}
                className={
                  n === page
                    ? "flex h-8 min-w-8 items-center justify-center rounded-lg bg-indigo-600 px-2 text-sm font-medium text-white"
                    : btn
                }
              >
                {n}
              </button>
            )
          )}
        </div>

        {/* compact indicator on mobile */}
        <span className="px-2 text-xs text-slate-500 sm:hidden">
          {page} / {totalPages}
        </span>

        <button
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          className={btn}
          aria-label="Next"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ---------- per-row action buttons (shared by table + cards) ---------- */

function RowActions({ item, onView, onEdit, onDelete, showView }) {
  return (
    <div className="flex items-center justify-end gap-1">
      {showView && (
        <button
          onClick={() => onView(item)}
          title="View"
          className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-700"
        >
          <Eye size={16} />
        </button>
      )}
      <button
        onClick={() => onEdit(item)}
        title="Edit"
        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
      >
        <Pencil size={16} />
      </button>
      <button
        onClick={() => onDelete(item)}
        title="Delete"
        className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

/* ================================================================== *
 *  MAIN PAGE
 * ================================================================== */

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [panel, setPanel] = useState({ mode: null, item: null });
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ---- silence the benign CRA "ResizeObserver loop" overlay ---- */
  useEffect(() => {
    const onErr = (e) => {
      const msg = e?.message || "";
      if (msg.includes("ResizeObserver loop")) {
        e.stopImmediatePropagation();
        // hide the dev-server error overlay if it popped up
        document
          .querySelectorAll(
            "#webpack-dev-server-client-overlay, #webpack-dev-server-client-overlay-div"
          )
          .forEach((el) => (el.style.display = "none"));
      }
    };
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
  }, []);

  /* ---- data loading ---- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Apirequests("testimonials/admin", "GET", {}, {
        authorization: `Bearer ${token}`,
      });
      if (res?.success) {
        setItems(Array.isArray(res?.data) ? res.data : []);
        setDemoMode(false);
      } else {
        setItems(DEMO_DATA);
        setDemoMode(true);
      }
    } catch {
      setItems(DEMO_DATA);
      setDemoMode(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- open helpers ---- */
  const openView = (item) => setPanel({ mode: "view", item });
  const openAdd = () => {
    setForm(emptyForm());
    setErrors({});
    setPanel({ mode: "add", item: null });
  };
  const openEdit = (item) => {
    setForm({
      type: item.type || "text",
      serialNo: item.serialNo ?? "",
      name: item.name || "",
      designation: item.designation || "",
      avatar: item.avatar || "",
      text: item.text || "",
      imgUrl: item.imgUrl || "",
      video: item.video || "",
      status: item.status || "active",
    });
    setErrors({});
    setPanel({ mode: "edit", item });
  };
  const closePanel = () => setPanel((p) => ({ ...p, mode: null }));

  /* ---- validation (mirrors the controller) ---- */
  const validate = () => {
    const e = {};
    if (form.serialNo === "" || form.serialNo === null) e.serialNo = "Serial number is required.";
    if (form.type === "video" && !form.video.trim()) e.video = "Video URL is required.";
    if (form.type === "text") {
      if (!form.text.trim()) e.text = "Review text is required.";
      if (!form.name.trim()) e.name = "Name is required.";
    }
    if (form.type === "image" && !form.imgUrl.trim()) e.imgUrl = "Image URL is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---- create / update ---- */
  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = { ...form, serialNo: Number(form.serialNo) };
    try {
      const url = panel.mode === "add" ? "testimonials/create" : `testimonials/edit/${panel.item._id}`;
      const method = panel.mode === "add" ? "POST" : "PUT";
      const res = await Apirequests(url, method, payload, { authorization: `Bearer ${token}` });
      if (res?.success) {
        SuccessTost(res?.message || "Testimonial saved successfully.");
        closePanel();
        await load();
      } else {
        ErrorTost(res?.message || "Something went wrong");
      }
    } catch (err) {
      setErrors({ form: err.message });
    } finally {
      setSaving(false);
    }
  };

  /* ---- delete ---- */
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await Apirequests(`delete/${toDelete._id}`, "DELETE", {}, {
        authorization: `Bearer ${token}`,
      });
      if (res?.success) {
        SuccessTost(res?.message || "Deleted successfully.");
      } else {
        ErrorTost(res?.message || "Could not delete.");
      }
      setToDelete(null);
      await load();
    } catch (err) {
      ErrorTost(err.message || "Internal server error");
    } finally {
      setDeleting(false);
    }
  };

  /* ---- derived list ---- */
  const filtered = items
    .filter((it) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (it.name || "").toLowerCase().includes(q) ||
        (it.designation || "").toLowerCase().includes(q) ||
        (it.type || "").toLowerCase().includes(q) ||
        (it.status || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (a.serialNo || 0) - (b.serialNo || 0));

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE);

  // reset to first page on new search; clamp page if the list shrank
  useEffect(() => setPage(1), [search]);
  useEffect(() => setPage((p) => Math.min(p, totalPages)), [totalPages]);

  const panelTitle =
    panel.mode === "add"
      ? "Add testimonial"
      : panel.mode === "edit"
      ? "Edit testimonial"
      : "Testimonial details";

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-10">
        {/* header */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">Testimonials</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage the reviews shown across Silva Method India.
              {demoMode && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
                  Demo data — API not reachable
                </span>
              )}
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 sm:w-auto"
          >
            <Plus size={17} />
            Add testimonial
          </button>
        </div>

        {/* search */}
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm sm:max-w-xs">
          <Search size={16} className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, type, status…"
            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
        </div>

        {/* list */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
              <Loader2 className="animate-spin" size={18} /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <Quote size={20} />
              </div>
              <p className="font-medium text-slate-700">No testimonials found</p>
              <p className="mt-1 text-sm text-slate-500">
                {search ? "Try a different search." : "Add your first one to get started."}
              </p>
            </div>
          ) : (
            <>
              {/* ---------- desktop / tablet: table ---------- */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60 text-xs font-medium uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Person</th>
                      <th className="px-5 py-3">Type</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Review</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((it) => (
                      <tr key={it._id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                        <td className="px-5 py-4 text-slate-400">{it.serialNo}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar name={it.name} src={it.avatar} size={36} />
                            <div>
                              <p className="font-medium text-slate-900">{it.name || "—"}</p>
                              {it.designation && <p className="text-xs text-slate-500">{it.designation}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <TypeBadge type={it.type} />
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={it.status} />
                        </td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => openView(it)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                          >
                            <Eye size={14} /> View
                          </button>
                        </td>
                        <td className="px-5 py-4">
                          <RowActions
                            item={it}
                            onView={openView}
                            onEdit={openEdit}
                            onDelete={setToDelete}
                            showView={false}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ---------- mobile: stacked cards ---------- */}
              <div className="divide-y divide-slate-100 md:hidden">
                {paged.map((it) => (
                  <div key={it._id} className="flex items-center gap-3 p-4">
                    <Avatar name={it.name} src={it.avatar} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-400">#{it.serialNo}</span>
                        <TypeBadge type={it.type} />
                        <StatusBadge status={it.status} />
                      </div>
                      <p className="mt-1 truncate font-medium text-slate-900">{it.name || "—"}</p>
                      {it.designation && <p className="truncate text-xs text-slate-500">{it.designation}</p>}
                    </div>
                    <RowActions
                      item={it}
                      onView={openView}
                      onEdit={openEdit}
                      onDelete={setToDelete}
                      showView={true}
                    />
                  </div>
                ))}
              </div>

              <Pagination page={page} totalPages={totalPages} total={filtered.length} onPage={setPage} />
            </>
          )}
        </div>
      </div>

      {/* slide-in panel — handles view / add / edit */}
      <SidePanel
        open={panel.mode !== null}
        title={panelTitle}
        subtitle={panel.mode === "view" ? null : "Fill in the details below"}
        onClose={closePanel}
      >
        {panel.mode === "view" && <TestimonialView item={panel.item} />}
        {(panel.mode === "add" || panel.mode === "edit") && (
          <>
            {errors.form && (
              <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{errors.form}</div>
            )}
            <TestimonialForm
              form={form}
              setForm={setForm}
              errors={errors}
              onSubmit={submit}
              saving={saving}
            />
          </>
        )}
      </SidePanel>

      <ConfirmDelete
        item={toDelete}
        busy={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}